import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, CheckCircle, XCircle, AlertCircle, User, Calendar, DollarSign, Building, Eye, FileText, ArrowRight } from 'lucide-react';
import { WorkflowService, PendingTask } from '../services/WorkflowService';
import { useAuth } from '../contexts/AuthContext';
import ModernLayout from '../components/ModernLayout';
import { ModernCard, ActionCard } from '../components/ModernCards';

const PendingTasksPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [pendingTasks, setPendingTasks] = useState<PendingTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadPendingTasks();
  }, []);

  const loadPendingTasks = async () => {
    try {
      setLoading(true);
      const tasks = await WorkflowService.getMyPendingTasks();
      setPendingTasks(tasks);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load pending tasks');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD'
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStepStatusIcon = (status: string) => {
    switch (status) {
      case 'PENDING':
        return <Clock className="h-5 w-5 text-yellow-500" />;
      case 'IN_PROGRESS':
        return <Clock className="h-5 w-5 text-green-500" />;
      default:
        return <Clock className="h-5 w-5 text-gray-500" />;
    }
  };

  const handleApprove = async (task: PendingTask) => {
    try {
      await WorkflowService.executeWorkflowAction(
        task.item_info?.expense?.id || 0,
        {
          step_instance_id: task.step_instance.id,
          action: 'approve'
        }
      );
      await loadPendingTasks(); // Reload tasks
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to approve task');
    }
  };

  const handleReject = async (task: PendingTask) => {
    try {
      await WorkflowService.executeWorkflowAction(
        task.item_info?.expense?.id || 0,
        {
          step_instance_id: task.step_instance.id,
          action: 'reject',
          rejection_reason: 'Rejected by approver'
        }
      );
      await loadPendingTasks(); // Reload tasks
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to reject task');
    }
  };

  if (loading) {
    return (
      <ModernLayout title="Loading..." subtitle="Fetching your pending tasks">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      </ModernLayout>
    );
  }

  return (
    <ModernLayout
      title="Pending Approvals"
      subtitle={`You have ${pendingTasks.length} pending approval task${pendingTasks.length !== 1 ? 's' : ''}`}
    >
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {pendingTasks.length === 0 ? (
        <ModernCard className="p-12 text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="h-8 w-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Pending Tasks</h3>
          <p className="text-gray-600">You're all caught up! No expenses require your approval at the moment.</p>
        </ModernCard>
      ) : (
        <div className="space-y-6">
          {pendingTasks.map((task) => (
            <ModernCard key={task.step_instance.id} className="p-6 hover:shadow-lg transition-shadow">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  {/* Task Header */}
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                      <FileText className="h-5 w-5 text-green-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">{task.item_info?.expense?.title || 'Unknown Expense'}</h3>
                      <p className="text-sm text-gray-600">Expense ID: #{task.item_info?.expense?.id || 'N/A'}</p>
                    </div>
                  </div>

                  {/* Task Details Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                        <DollarSign className="h-4 w-4 text-green-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-700">Amount</p>
                        <p className="text-lg font-semibold text-gray-900">
                          {formatCurrency(task.item_info?.expense?.amount || 0, task.item_info?.expense?.currency || 'USD')}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                        <User className="h-4 w-4 text-purple-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-700">Submitted by</p>
                        <p className="text-lg font-semibold text-gray-900">User #{task.workflow_instance.initiated_by}</p>
                      </div>
                    </div>

                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                        <Calendar className="h-4 w-4 text-orange-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-700">Submitted</p>
                        <p className="text-lg font-semibold text-gray-900">
                          {formatDate(task.workflow_instance.started_at)}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Step Information */}
                  <div className="bg-gray-50 rounded-lg p-4 mb-6">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-sm font-medium text-gray-700">Current Step</h4>
                    </div>
                    <div className="flex items-center space-x-2">
                      {getStepStatusIcon(task.step_instance.status)}
                      <span className="text-sm text-gray-900">{task.step_instance.step_name}</span>
                    </div>
                    {task.step_instance.step_notes && (
                      <p className="text-sm text-gray-600 mt-2">{task.step_instance.step_notes}</p>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <button
                        onClick={() => navigate(`/expenses/${task.item_info?.expense?.id}`)}
                        className="btn-secondary flex items-center space-x-2"
                      >
                        <Eye className="h-4 w-4" />
                        <span>View Details</span>
                      </button>
                    </div>
                    
                    <div className="flex items-center space-x-3">
                      <button
                        onClick={() => handleApprove(task)}
                        className="btn-primary flex items-center space-x-2"
                      >
                        <CheckCircle className="h-4 w-4" />
                        <span>Approve</span>
                      </button>
                      
                      <button
                        onClick={() => handleReject(task)}
                        className="btn-ghost text-red-600 hover:text-red-700 hover:bg-red-50 flex items-center space-x-2"
                      >
                        <XCircle className="h-4 w-4" />
                        <span>Reject</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </ModernCard>
          ))}
        </div>
      )}
    </ModernLayout>
  );
};

export default PendingTasksPage;